import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Hero.css';
import { AnimatedBackground } from '../Background/AnimatedBackground';

const Hero = () => {
  const phrases = [
    'Smart Manufacturing',
    'Industrial Automation',
    'Digital Transformation',
    'Intelligent IoT',
  ];

  const [displayText, setDisplayText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex];

    if (isPaused) {
      const t = setTimeout(() => { setIsPaused(false); setIsDeleting(true); }, 1800);
      return () => clearTimeout(t);
    }

    const speed = isDeleting ? 55 : 90;
    const t = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < current.length) {
          setDisplayText(current.slice(0, charIndex + 1));
          setCharIndex(c => c + 1);
        } else {
          setIsPaused(true);
        }
      } else {
        if (charIndex > 0) {
          setDisplayText(current.slice(0, charIndex - 1));
          setCharIndex(c => c - 1);
        } else {
          setIsDeleting(false);
          setPhraseIndex(i => (i + 1) % phrases.length);
        }
      }
    }, speed);

    return () => clearTimeout(t);
  }, [charIndex, isDeleting, isPaused, phraseIndex]);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.4, 0.25, 1],
      },
    },
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToAssessment = () => {
    document.getElementById('factory-assessment')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="hero" ref={ref}>
      <div className="hero-container">
        <motion.div
          className="hero-content-right"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.div className="hero-badge" variants={itemVariants}>
            <span className="badge-text">Industrial Automation Excellence</span>
          </motion.div>

          <motion.div className="hero-offer-badge" variants={itemVariants}>
            <span className="offer-badge-icon">✓</span>
            <span className="offer-badge-text">Free Factory Assessment Available — No Commitment</span>
          </motion.div>

          <motion.div className="hero-headline-backdrop" variants={itemVariants}>
            <h1 className="hero-title">
              Engineering the Future
              <span className="typewriter-line">
                of <span className="gradient-text">{displayText}<span className="typewriter-cursor">|</span></span>
              </span>
            </h1>
            <p className="hero-subtitle">
              Transforming industries through cutting-edge automation, AI-powered analytics, 
              and intelligent IoT solutions. Your partner in digital transformation and industrial innovation.
            </p>
          </motion.div>

          <motion.div className="hero-buttons" variants={itemVariants}>
            <motion.button
              className="btn-primary btn-hero-cta"
              onClick={scrollToAssessment}
              animate={{
                scale: [1, 1.045, 1],
                boxShadow: [
                  '0 4px 20px rgba(255, 107, 0, 0.5)',
                  '0 4px 40px rgba(255, 140, 0, 0.9)',
                  '0 4px 20px rgba(255, 107, 0, 0.5)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              whileHover={{ scale: 1.09, y: -4 }}
              whileTap={{ scale: 0.96 }}
            >
              Book Free Visit
            </motion.button>
            <button className="btn-secondary" onClick={scrollToServices}>
              Explore Solutions
            </button>
          </motion.div>
        </motion.div>

        <motion.div 
          className="hero-stats-left" 
          variants={itemVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <div className="stat-item">
            <div className="stat-number">10+</div>
            <div className="stat-label">Years Experience</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">50+</div>
            <div className="stat-label">Projects Delivered</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">100%</div>
            <div className="stat-label">Client Satisfaction</div>
          </div>
        </motion.div>
      </div>

      <AnimatedBackground variant="hero" />
    </section>
  );
};

export default Hero;
